import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { assetPreviewBlob, type NoteAsset } from '../assets/assetTypes'
import { assetAspect, isTallAspect, stageTiles, type ImageStageLook } from '../images/imageStage'
import { Icon } from './Icon'
import { enlargedPastSharp } from '../utils/imageSharpness'
import type { ImagePreview } from './ImageLightbox'

/**
 * A note's images where the note is read: a large picture or a few equal tiles above the text, a one-row band when the words
 * lead (phone), or a column beside the text (desktop). Sizes come from CSS (design/studio/image-stage.css) so a resized window
 * lays out again without a listener; this file decides only what is shown and what a tap does.
 */
export interface NoteImageStageProps {
  /** `stage` is above the text; `aside` is the desktop column beside it. */
  placement: 'stage' | 'aside'
  /** `large` shows the pictures, `compact` folds them to a band (stage only). */
  look: ImageStageLook
  assets: readonly NoteAsset[]
  /** Pictures the note holds, saved or not: the ones still arriving show as quiet tiles. */
  expectedCount: number
  remoteUrls: ReadonlyMap<string, string>
  highlightedAssetId?: string | null
  highlightAnimationMs?: number
  onLook: (look: ImageStageLook) => void
  onRemove: (asset: NoteAsset) => void
  onPreviewOpen: (preview: ImagePreview) => void
  onPreviewVisible?: (asset: NoteAsset) => void
  /** Fetches the full-size picture for a hero or a column, which are big enough to need it. */
  loadFullImage?: (asset: NoteAsset) => Promise<Blob>
  /** Unpack is reading these screenshots now: a scan passes over them (design/studio/screenshot-cleanup.css). */
  reading?: boolean
}

const MASTER_REQUESTS = new Map<string, Promise<Blob>>()
const MASTER_REQUEST_LIMIT = 6

function requestMaster(asset: NoteAsset, load: (asset: NoteAsset) => Promise<Blob>): Promise<Blob> {
  let request = MASTER_REQUESTS.get(asset.id)
  if (!request) {
    request = load(asset)
    MASTER_REQUESTS.set(asset.id, request)
    request.catch(() => MASTER_REQUESTS.delete(asset.id))
    while (MASTER_REQUESTS.size > MASTER_REQUEST_LIMIT) MASTER_REQUESTS.delete(MASTER_REQUESTS.keys().next().value as string)
  }
  return request
}

function useObjectUrl(blob: Blob | null): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useLayoutEffect(() => {
    if (!blob) { setUrl(null); return }
    let next: string
    try { next = URL.createObjectURL(blob) } catch { setUrl(null); return }
    setUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [blob])
  return url
}

/** The thumbnail right away; the full picture (when `full`) replaces it as soon as it is on the device or has been fetched. */
function useStageSource(asset: NoteAsset, remoteUrl: string | undefined, full: boolean, load: NoteImageStageProps['loadFullImage']): string | null {
  const [fetched, setFetched] = useState<{ id: string; blob: Blob } | null>(null)
  const localMaster = asset.processedMasterBlob ?? asset.sourceBlob
  useEffect(() => {
    if (!full || localMaster || !load || !asset.masterStoragePath) return
    let live = true
    requestMaster(asset, load).then((blob) => { if (live) setFetched({ id: asset.id, blob }) }, () => undefined)
    return () => { live = false }
  }, [asset, full, load, localMaster])
  const blob = full
    ? localMaster ?? (fetched?.id === asset.id ? fetched.blob : null) ?? assetPreviewBlob(asset, true)
    : assetPreviewBlob(asset, true)
  return useObjectUrl(blob) ?? remoteUrl ?? null
}

type PictureKind = 'hero' | 'tile' | 'solo'

function Picture({ asset, kind, remoteUrl, more, highlighted, highlightAnimationMs, full, onRemove, onOpen, onVisible, load }: {
  asset: NoteAsset
  kind: PictureKind
  remoteUrl?: string
  /** "+N": this tile stands for the rest too. */
  more?: number
  highlighted: boolean
  highlightAnimationMs?: number
  full: boolean
  onRemove: (asset: NoteAsset) => void
  onOpen: (preview: ImagePreview) => void
  onVisible?: (asset: NoteAsset) => void
  load: NoteImageStageProps['loadFullImage']
}) {
  const [unfolded, setUnfolded] = useState(false)
  // A tile shows the thumbnail, unless it is shown larger than the thumbnail is: then the full picture, so it stays sharp.
  const [enlarged, setEnlarged] = useState(false)
  const source = useStageSource(asset, remoteUrl, full || enlarged, load)
  const imageRef = useRef<HTMLImageElement>(null)
  useEffect(() => {
    const image = imageRef.current
    if (!image || full || enlarged || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => { if (enlargedPastSharp(image)) setEnlarged(true) })
    observer.observe(image)
    return () => observer.disconnect()
  }, [full, enlarged, source])
  const aspect = assetAspect(asset)
  const tall = isTallAspect(aspect)
  const alt = asset.originalFileName ?? 'Note image'
  const saved = asset.state === 'synced' && Boolean(asset.thumbnailStoragePath ?? asset.masterStoragePath)
  const className = ['image-stage__picture', `image-stage__picture--${kind}`, highlighted && 'image-stage__picture--revealed'].filter(Boolean).join(' ')
  return (
    <figure
      className={className}
      style={{ '--ar': aspect, ...(highlighted && highlightAnimationMs !== undefined ? { animationDuration: `${highlightAnimationMs}ms` } : {}) } as CSSProperties}
      data-asset-id={asset.id}
      data-tall={tall || undefined}
      data-unfolded={unfolded || undefined}
    >
      {source ? (
        <button type="button" className="image-stage__open" onClick={() => onOpen({ assetId: asset.id, source, alt })} aria-label={`Open ${alt}`}>
          <img ref={imageRef} src={source} alt={alt} decoding="async" onLoad={(event) => { onVisible?.(asset); if (!full && enlargedPastSharp(event.currentTarget)) setEnlarged(true) }} />
          {more ? <span className="image-stage__more" aria-hidden="true">+{more}</span> : null}
        </button>
      ) : saved
        ? <span className="image-stage__placeholder skeleton" role="status" aria-label="Loading saved image" />
        : <span className="image-stage__placeholder">Image not yet synchronized</span>}
      {kind === 'hero' && tall && source ? (
        <button type="button" className="image-stage__unfold" aria-expanded={unfolded} onClick={() => setUnfolded((open) => !open)}>
          <Icon name="chevron" /><span>{unfolded ? 'Show less' : 'Show full image'}</span>
        </button>
      ) : null}
      <button type="button" className="image-stage__remove" onClick={() => onRemove(asset)} aria-label="Remove image" title="Remove image"><Icon name="trash" /></button>
    </figure>
  )
}

function Thumb({ asset, remoteUrl }: { asset: NoteAsset; remoteUrl?: string }) {
  const source = useStageSource(asset, remoteUrl, false, undefined)
  return <span className="image-stage__thumb" style={{ '--ar': assetAspect(asset) } as CSSProperties}>{source ? <img src={source} alt="" decoding="async" /> : null}</span>
}

const count = (total: number) => `${total} image${total === 1 ? '' : 's'}`

/** The phone keyboard shows the text under the caret; closing it is how the pictures come back. */
function closeKeyboard() {
  if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
}

export function NoteImageStage(props: NoteImageStageProps) {
  const { placement, look, assets, expectedCount, remoteUrls, highlightedAssetId = null, highlightAnimationMs, onLook, onRemove, onPreviewOpen, onPreviewVisible, loadFullImage, reading = false } = props
  const visible = assets.filter((asset) => asset.state !== 'deleted')
  const total = Math.max(expectedCount, visible.length)
  if (total === 0) return null
  const pending = total - visible.length
  const label = count(total)
  const picture = (asset: NoteAsset, kind: PictureKind, extra?: { more?: number }) => (
    <Picture
      key={asset.id}
      asset={asset}
      kind={kind}
      remoteUrl={remoteUrls.get(asset.id)}
      more={extra?.more}
      highlighted={asset.id === highlightedAssetId}
      highlightAnimationMs={highlightAnimationMs}
      full={kind !== 'tile' || placement === 'aside'}
      onRemove={onRemove}
      onOpen={onPreviewOpen}
      onVisible={onPreviewVisible}
      load={loadFullImage}
    />
  )
  const pendingTiles = Array.from({ length: pending }, (_, index) => (
    <figure className="image-stage__picture image-stage__picture--tile image-stage__picture--pending" key={`pending-${index}`} style={{ '--ar': 4 / 3 } as CSSProperties}>
      <span className="image-stage__placeholder">Image syncing…</span>
    </figure>
  ))

  if (placement === 'aside') {
    return (
      <section className="image-aside" aria-label="Images in this note" data-reading={reading || undefined} aria-busy={reading || undefined}>
        <h2 className="image-aside__heading">{label}</h2>
        <div className="image-gallery image-gallery--aside" data-count={total}>
          {visible.map((asset) => picture(asset, total === 1 ? 'solo' : 'tile'))}
          {pendingTiles}
        </div>
      </section>
    )
  }

  const thumbs = (
    <span className="image-stage__thumbs" aria-hidden="true">
      {visible.slice(0, 7).map((asset) => <Thumb key={asset.id} asset={asset} remoteUrl={remoteUrls.get(asset.id)} />)}
      {total > 7 ? <span className="image-stage__thumb image-stage__thumb--more">+{total - 7}</span> : null}
    </span>
  )
  const fold = (
    <button type="button" className="image-stage__fold" onClick={closeKeyboard}>
      {thumbs}
      <span className="image-stage__fold-label"><strong>{label}</strong><small>Hidden while typing</small></span>
      <span className="image-stage__action">Show<Icon name="chevron" /></span>
    </button>
  )

  if (look === 'compact') {
    return (
      <section className="image-stage image-stage--compact" aria-label="Images in this note">
        <button type="button" className="image-stage__band" aria-label={`Show ${label}`} onClick={() => onLook('large')}>
          {thumbs}
          <span className="image-stage__band-label"><strong>{label}</strong><small>Tap to show</small></span>
          <span className="image-stage__action">Show<Icon name="chevron" /></span>
        </button>
        {fold}
      </section>
    )
  }

  const single = total === 1 && visible.length === 1
  const { shown, more } = stageTiles(visible)
  const allTall = !single && visible.length > 0 && visible.every((asset) => isTallAspect(assetAspect(asset)))
  return (
    <section className="image-stage image-stage--large" aria-label="Images in this note" data-reading={reading || undefined} aria-busy={reading || undefined}>
      <div className="image-stage__body">
        {single ? picture(visible[0]!, 'hero') : (
          <div className="image-gallery" data-count={Math.min(total, 4)} data-tall={allTall || undefined}>
            {shown.map((asset, index) => picture(asset, 'tile', more && index === shown.length - 1 ? { more } : undefined))}
            {pendingTiles}
          </div>
        )}
        <button type="button" className="image-stage__look" onClick={() => onLook('compact')} aria-label="Hide images" title="Hide images">
          <Icon name="view-off" /><span>Hide</span>
        </button>
      </div>
      {fold}
    </section>
  )
}
